import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Package, MapPin, Heart, Settings, LogOut, Gift, Copy } from "lucide-react";
import api, { money } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { StarRating } from "@/components/Badges";
import { toast } from "sonner";

export default function Account() {
  const { user, logout } = useAuth();
  const [tab, setTab] = useState("products");
  const [data, setData] = useState({ orders: [], passes: [] });

  useEffect(() => { api.get("/me/friibs").then((r) => setData(r.data)); }, []);

  const refLink = `${window.location.origin}/?ref=${user?.referral_code || ""}`;
  const copy = () => { navigator.clipboard.writeText(refLink); toast.success("Referral link copied!"); };

  const passesBy = (status) => data.passes.filter((p) => (status === "ready" ? p.status === "ready" : status === "used" ? p.status === "used" : p.status === "expired"));

  return (
    <div className="max-w-6xl mx-auto px-4 sm:px-6 py-8">
      <div className="flex items-center justify-between mb-8">
        <div>
          <h1 className="font-display text-3xl lg:text-4xl font-extrabold text-stone-900">Hi, {user?.name?.split(" ")[0]} 👋</h1>
          <p className="text-stone-500">Your Friibs, all in one place.</p>
        </div>
        <button onClick={logout} data-testid="logout-btn" className="inline-flex items-center gap-1.5 px-4 py-2 rounded-full font-semibold text-stone-600 bg-stone-100 hover:bg-stone-200"><LogOut size={16} /> Sign out</button>
      </div>

      {/* referral */}
      <div className="rounded-3xl p-6 text-white mb-8 flex flex-col sm:flex-row sm:items-center gap-4 justify-between" style={{ background: "var(--ink)" }}>
        <div className="flex items-center gap-3"><Gift size={22} style={{ color: "var(--friib)" }} /><div><p className="font-display font-bold">Invite friends to Friib</p><p className="text-stone-300 text-sm">Give your friends their next favorite find.</p></div></div>
        <div className="flex gap-2"><input readOnly value={refLink} className="px-4 py-2.5 rounded-full text-stone-900 text-sm w-56" data-testid="account-referral" /><button onClick={copy} className="px-4 py-2.5 rounded-full font-bold flex items-center gap-1.5" style={{ background: "var(--friib)" }}><Copy size={15} /></button></div>
      </div>

      <div className="flex gap-2 mb-6 overflow-x-auto no-scrollbar">
        {[["products", "Product Friibs", Package], ["local", "Local Friibs", MapPin]].map(([k, l, Icon]) => (
          <button key={k} onClick={() => setTab(k)} data-testid={`account-tab-${k}`}
            className={`shrink-0 inline-flex items-center gap-2 px-5 py-2.5 rounded-full font-bold ${tab === k ? "text-white" : "bg-stone-100 text-stone-600"}`} style={{ background: tab === k ? "var(--friib)" : undefined }}>
            <Icon size={16} /> {l}
          </button>
        ))}
      </div>

      {tab === "products" ? (
        data.orders.length === 0 ? <Empty text="No product Friibs yet." cta="/shop" ctaLabel="Shop Friibs" /> :
        <div className="space-y-3">
          {data.orders.map((o) => (
            <div key={o.id} className="rounded-2xl bg-white border border-stone-100 friib-shadow p-4 flex items-center gap-4" data-testid={`order-${o.id.slice(0,8)}`}>
              <img src={o.product_image} alt="" className="w-16 h-16 object-cover rounded-xl" />
              <div className="flex-1"><p className="font-display font-bold text-stone-900">{o.product_name}</p><p className="text-sm text-stone-400">Order #{o.id.slice(0, 8)} · {o.upsells?.length || 0} add-ons · {money(o.total)}</p></div>
              <span className="px-3 py-1 rounded-full text-xs font-bold bg-emerald-100 text-emerald-700 capitalize">{o.status}</span>
            </div>
          ))}
        </div>
      ) : (
        <div className="space-y-6">
          {["ready", "used", "expired"].map((st) => {
            const list = passesBy(st);
            if (!list.length) return null;
            return (
              <div key={st}>
                <h3 className="font-display font-bold text-stone-900 mb-2 capitalize">{st === "ready" ? "Ready to use" : st}</h3>
                <div className="grid sm:grid-cols-2 gap-3">
                  {list.map((p) => (
                    <Link key={p.id} to={`/pass/${p.id}`} className="rounded-2xl bg-white border border-stone-100 friib-shadow p-4 flex items-center gap-4 hover:friib-shadow-hover" data-testid={`pass-${p.id.slice(0,8)}`}>
                      <img src={p.offer_image} alt="" className="w-16 h-16 object-cover rounded-xl" />
                      <div className="flex-1"><p className="font-display font-bold text-stone-900">{p.offer_title}</p><p className="text-sm text-stone-400">{p.business_name} · {p.code}</p></div>
                    </Link>
                  ))}
                </div>
              </div>
            );
          })}
          {data.passes.length === 0 && <Empty text="No local Friibs yet." cta="/local" ctaLabel="Explore Nearby" />}
        </div>
      )}
    </div>
  );
}

function Empty({ text, cta, ctaLabel }) {
  return (
    <div className="rounded-3xl border-2 border-dashed border-stone-200 p-16 text-center">
      <p className="text-stone-400 mb-4">{text}</p>
      <Link to={cta} className="px-6 py-3 rounded-full font-bold text-white inline-block" style={{ background: "var(--friib)" }}>{ctaLabel}</Link>
    </div>
  );
}
